/**
 * Product-content AI boundary.
 *
 * Canonical allowlist + payload filters for `product.enrich_content`.
 * Keep identical to `supabase/functions/_shared/product-ai-boundary.ts`.
 *
 * Marketing drafts are never authority for SKU, GTIN, brand, dimensions,
 * materials (unless already supplied as facts), compliance, origin, stock,
 * price, or supplier data.
 */

import {
  SHORT_DESCRIPTION_MAX_LENGTH,
  applyGroundedCopyQuality,
  buildGroundedDescription,
  buildGroundedMetaDescription,
  buildGroundedSeoTitle,
  truncateAtWordBoundary,
} from "./product-ai-copy-quality";

export const PRODUCT_ENRICH_TASK = "product.enrich_content";
export const ORPHANED_GENERATE_DESCRIPTION_TASK = "product.generate_description";

/** Only these task ids may call an external model when a provider key exists. */
export const EXTERNAL_AI_ALLOWLIST = [PRODUCT_ENRICH_TASK] as const;

export const FAIL_CLOSED_EXTERNAL_TASKS = [
  "inventory.stock_summary",
  "ops.daily_brief",
  "orders.exception_summary",
  "supplier.review_draft",
] as const;

export const PRODUCT_FACT_KEYS = [
  "name",
  "brand",
  "category_name",
  "product_type",
  "option_names",
  "option_values",
] as const;

export const REWRITE_COPY_KEYS = [
  "description",
  "short_description",
  "seo_title",
  "meta_description",
  "tags",
  "seo_keywords",
] as const;

export const MARKETING_OUTPUT_KEYS = [
  "description",
  "short_description",
  "seo_title",
  "meta_description",
  "tags",
  "seo_keywords",
] as const;

export const FORBIDDEN_PRODUCT_CONTEXT_KEYS = [
  "sku",
  "gtin",
  "upc",
  "ean",
  "barcode",
  "cost_price",
  "selling_price",
  "price",
  "stock",
  "on_hand",
  "available",
  "reorder_point",
  "supplier",
  "supplier_id",
  "customer",
  "order",
  "connector",
  "credential",
  "token",
  "access_token",
  "refresh_token",
  "api_key",
  "secret",
  "weight_kg",
  "length_cm",
  "width_cm",
  "height_cm",
  "country_of_origin",
  "mpn",
  "manufacturer",
] as const;

/**
 * Appended to the product.enrich_content system prompt on every external call.
 *
 * Primary grounding contract is prompt-enforced: every factual/benefit claim must
 * be directly traceable to supplied facts (or existing_copy when rewrite=true).
 * A finite known-trope stripper is defence-in-depth only — it is NOT a general
 * semantic claim validator and cannot mechanically catch arbitrary unsupported claims.
 */
export const PRODUCT_CONTENT_HARD_RULES =
  "Draft marketing and SEO text using ONLY the supplied product facts"
  + " (and existing_copy when rewrite is true). Restate allowed facts conservatively."
  + " Every factual or product-benefit claim must be directly traceable to a supplied"
  + " fact or to operator-provided copy. Do NOT invent performance, fit, comfort,"
  + " durability, safety, quality, compliance, material, construction, origin,"
  + " suitability, benefit, or lifestyle claims unless that exact claim already appears"
  + " in the supplied facts or operator-provided copy."
  + " A supplied feature does NOT authorize inventing its consequence or benefit."
  + " Example: if facts include an elastic gusset, you may restate that feature, but you"
  + " must NOT infer improves mobility, enhances comfort, provides secure fit, or similar"
  + " benefits unless those benefits themselves were supplied."
  + " When rewrite is true, operator copy authorizes only the claim text actually present"
  + " in that copy — it does not authorize additional consequences, benefits, or safety"
  + " claims merely because a matching phrase appears somewhere in the source text."
  + " Option values (for example colours or size labels such as 12 year / 14 year) may be"
  + " listed only as available options — do not interpret them into age, rider, youth,"
  + " young riders, or performance suitability. Keep labels literal; prefer natural grammar"
  + " such as 'available in Black and Brown, with 12 year and 14 year size options'."
  + " Category names may be used naturally in customer-facing sentences but do not infer"
  + " product benefits from the category alone, and never use internal/database phrasing"
  + " such as 'falls under the X category', 'belongs to the category', or 'product category is'."
  + " Brand, name, category, and options may be used."
  + " If facts are sparse, write concise natural customer-facing copy"
  + " — never embellished marketing language."
  + " SEO titles must stay within 60 characters, never truncate mid-word, and must not leave"
  + " dangling fragments (for example 'Bridle P'). Prefer product name and brand; remove"
  + " lower-value suffixes before truncating the product name. Do not invent descriptors"
  + " merely to fill space."
  + " Meta descriptions must be factual from permitted facts only — do not use unsupported"
  + " adjectives such as dependable, premium, high quality, ideal, perfect, durable, or"
  + " comfortable."
  + " Keywords and tags must come from product name, brand, category, and option values."
  + " Reject vague demographics (for example Unisex) and invented lifestyle phrases"
  + " (for example Youth Riding Gear) unless explicitly supplied. Prefer literal option"
  + " labels over mechanical phrases such as '12 year size'."
  + " When rewrite is true, preserve factual operator claims and improve grammar/structure,"
  + " but regenerate poor, truncated, or generic SEO title/meta from grounded facts rather"
  + " than copying broken legacy SEO. Do not add new consequences or benefits."
  + " Forbidden unsupported examples unless explicitly supplied: perfect fit, secure fit,"
  + " ease of movement, ensuring ease of movement, designed for comfort, ideal for riders,"
  + " functionality and elegance, enhances rider stability, durable for everyday riding,"
  + " provides added support, helps reduce rubbing, flexible construction improves mobility,"
  + " suitable for demanding training, dependable, young riders, practical choice,"
  + " caters to different style preferences, right fit, versatile choice, suitable fit."
  + " Return description, short_description, seo_title, meta_description, tags, and"
  + " seo_keywords as separate fields. The description field must contain customer-facing"
  + " product copy only; never append SEO title, meta description, keywords, tags, or field"
  + " labels to it. Do not invent or change SKU, GTIN, UPC, EAN, brand, dimensions, weight,"
  + " materials that were not supplied, certifications, compliance claims, country of origin,"
  + " stock, price, or supplier facts. Do not return those fields."
  + " Output is a draft for operator review, never product-master authority.";

/**
 * Finite defence-in-depth blacklist of known unsupported marketing tropes.
 * Not a complete catalogue of prohibited claims — arbitrary semantic inventions
 * remain prompt-enforced only.
 */
export const KNOWN_UNSUPPORTED_TROPE_PHRASES = [
  "perfect fit",
  "secure fit",
  "comfortable fit",
  "ease of movement",
  "ensuring ease of movement",
  "designed for comfort",
  "all-day comfort",
  "ideal for riders",
  "functionality and elegance",
  "superior quality",
  "premium quality",
  "built to last",
  "ultimate comfort",
  "maximum comfort",
  "perfect for",
  "ensures a",
  "providing a",
  "enhances rider stability",
  "enhance rider stability",
  "flexible construction improves mobility",
  "improves mobility",
  "enhances comfort",
  "enhance comfort",
  "durable for everyday riding",
  "helps reduce rubbing",
  "help reduce rubbing",
  "provides added support",
  "provide added support",
  "suitable for demanding training",
  "enhanced stability",
  "enhanced safety",
  "dependable",
  "young riders",
  "youth riding",
  "practical choice",
  "style preferences",
  "right fit",
  "versatile choice",
  "suitable fit",
] as const;

/** @deprecated Use KNOWN_UNSUPPORTED_TROPE_PHRASES — name overstated prior scope. */
export const UNGROUNDED_CLAIM_PHRASES = KNOWN_UNSUPPORTED_TROPE_PHRASES;

export function isExternalAiAllowed(taskId: string): boolean {
  return (EXTERNAL_AI_ALLOWLIST as readonly string[]).includes(taskId);
}

function collectAuthorizationStrings(value: unknown, out: string[]): void {
  if (typeof value === "string") {
    const t = value.trim();
    if (t) out.push(t);
    return;
  }
  if (!value || typeof value !== "object") return;
  if (Array.isArray(value)) {
    for (const item of value) collectAuthorizationStrings(item, out);
    return;
  }
  for (const v of Object.values(value as Record<string, unknown>)) {
    collectAuthorizationStrings(v, out);
  }
}

function normalizeClaimText(text: string): string {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Lowercased authorization corpus of permitted facts (+ existing_copy when rewrite).
 * Used only for whole-sentence authorization of known tropes — not phrase-level
 * permission to invent consequences around a matching substring.
 */
export function buildClaimAuthorizationCorpus(context: unknown): string {
  const parts: string[] = [];
  if (!context || typeof context !== "object") return "";
  const ctx = context as Record<string, unknown>;
  collectAuthorizationStrings(ctx.facts, parts);
  if (ctx.rewrite === true) collectAuthorizationStrings(ctx.existing_copy, parts);
  return parts.join("\n").toLowerCase();
}

/** @deprecated Use buildClaimAuthorizationCorpus. */
export function buildGroundingCorpus(context: unknown): string {
  return buildClaimAuthorizationCorpus(context);
}

export function findKnownUnsupportedTropePhrases(text: string): string[] {
  const hay = normalizeClaimText(text ?? "");
  if (!hay) return [];
  return KNOWN_UNSUPPORTED_TROPE_PHRASES.filter((phrase) => hay.includes(phrase));
}

/**
 * True only when the full sentence (normalized) already appears in the
 * authorization corpus. Phrase substring matches do NOT authorize expanded claims.
 */
export function sentenceAuthorizedByCorpus(sentence: string, corpus: string): boolean {
  const normalizedSentence = normalizeClaimText(sentence.replace(/[.!?]+$/g, ""));
  const normalizedCorpus = normalizeClaimText(corpus ?? "");
  if (!normalizedSentence || !normalizedCorpus) return false;
  return normalizedCorpus.includes(normalizedSentence);
}

/**
 * Drop sentences that contain known unsupported tropes unless the entire
 * sentence is already present in the authorization corpus (exact restatement).
 * This is defence-in-depth for the finite trope list only.
 */
export function stripKnownUnsupportedTropeSentences(text: string, corpus: string): string {
  const raw = (text ?? "").trim();
  if (!raw) return "";
  const sentences = raw.split(/(?<=[.!?])\s+/);
  const kept = sentences.filter((sentence) => {
    const hits = findKnownUnsupportedTropePhrases(sentence);
    if (hits.length === 0) return true;
    return sentenceAuthorizedByCorpus(sentence, corpus);
  });
  return kept.join(" ").replace(/\s+/g, " ").trim();
}

export const SAFE_GROUNDED_COPY_GLUE_WORDS = [
  "a", "an", "the", "this", "these", "it", "its", "by", "from", "for", "of", "in", "on",
  "with", "and", "or", "as", "at", "to", "is", "are", "be", "available", "comes", "come",
  "offered", "option", "options", "size", "sizes", "colour", "colours", "color", "colors",
  "range", "various", "multiple", "including", "include", "includes", "choose", "different",
  "such", "both", "product", "category",
] as const;

function claimTokens(text: string): string[] {
  return (text.toLowerCase().match(/[a-z0-9]+/g) ?? []).filter(Boolean);
}

function factTokenSet(context: unknown): Set<string> {
  const out = new Set<string>();
  if (!context || typeof context !== "object") return out;
  const ctx = context as Record<string, unknown>;
  const parts: string[] = [];
  collectAuthorizationStrings(ctx.facts, parts);
  for (const part of parts) {
    for (const token of claimTokens(part)) out.add(token);
  }
  return out;
}

/**
 * For fresh generation (rewrite=false), reject any sentence that introduces
 * lexical content outside the supplied product facts plus a small neutral grammar
 * vocabulary. This is deliberately stricter than the finite trope list: when facts
 * are sparse, the model may restate them naturally but must not add new descriptors,
 * benefits, audiences, suitability, preferences, needs, or other claims.
 *
 * Rewrite requests are excluded because operator copy is explicitly supplied as
 * authorization and may contain a much wider vocabulary.
 */
export function stripLexicallyUngroundedFreshCopy(text: string, context: unknown): string {
  const raw = (text ?? "").trim();
  if (!raw) return "";
  const ctx = context && typeof context === "object" ? context as Record<string, unknown> : {};
  if (ctx.rewrite === true) return raw;

  const allowed = factTokenSet(context);
  for (const word of SAFE_GROUNDED_COPY_GLUE_WORDS) allowed.add(word);

  const sentences = raw.split(/(?<=[.!?])\s+/);
  const kept = sentences.filter((sentence) => {
    const tokens = claimTokens(sentence);
    return tokens.length > 0 && tokens.every((token) => allowed.has(token));
  });
  return kept.join(" ").replace(/\s+/g, " ").trim();
}

export function groundFreshDescriptionBody(text: string, context: unknown): string {
  const corpus = buildClaimAuthorizationCorpus(context);
  return stripLexicallyUngroundedFreshCopy(
    stripKnownUnsupportedTropeSentences(text, corpus),
    context,
  );
}


/** @deprecated Use findKnownUnsupportedTropePhrases / stripKnownUnsupportedTropeSentences. */
export function findUngroundedClaimPhrases(text: string, corpus: string): string[] {
  const hits = findKnownUnsupportedTropePhrases(text);
  // Preserve prior call shape for transitional tests: only report hits not authorized
  // as a whole-sentence restatement when corpus is provided.
  if (!corpus) return hits;
  if (hits.length === 0) return [];
  if (sentenceAuthorizedByCorpus(text, corpus)) return [];
  return hits;
}

/** @deprecated Use stripKnownUnsupportedTropeSentences. */
export function stripUngroundedClaimSentences(text: string, corpus: string): string {
  return stripKnownUnsupportedTropeSentences(text, corpus);
}

/** Concise natural customer-facing copy from permitted facts only — no embellished claims. */
export function buildNeutralProductCopy(facts: {
  name?: string | null;
  brand?: string | null;
  category?: string | null;
  options?: string[];
  values?: string[];
  optionGroups?: Record<string, string[]>;
}): MarketingDraftFields {
  const values = (facts.values ?? []).map((v) => String(v).trim()).filter(Boolean);
  const normalized = {
    name: facts.name,
    brand: facts.brand,
    category: facts.category,
    options: facts.options,
    values,
    optionGroups: facts.optionGroups,
  };
  const description = buildGroundedDescription(normalized);
  const short_description = truncateAtWordBoundary(description, SHORT_DESCRIPTION_MAX_LENGTH);
  const seo_title = buildGroundedSeoTitle(normalized);
  const meta_description = buildGroundedMetaDescription(normalized);
  return applyGroundedCopyQuality({
    description,
    short_description,
    seo_title,
    meta_description,
    tags: [],
    seo_keywords: "",
  }, normalized);
}

function extraAuthorizedTermsFromContext(context: unknown): string[] {
  if (!context || typeof context !== "object") return [];
  const ctx = context as Record<string, unknown>;
  const parts: string[] = [];
  collectAuthorizationStrings(ctx.facts, parts);
  if (ctx.rewrite === true) collectAuthorizationStrings(ctx.existing_copy, parts);
  return parts;
}

/**
 * Defence-in-depth: strip sentences containing known unsupported marketing tropes
 * unless the entire sentence is an authorized restatement already present in facts
 * or rewrite operator copy.
 *
 * Fresh generation also applies a strict lexical fact guard so novel descriptors
 * outside the supplied product facts are rejected even when they are not in the
 * finite trope list. Rewrite requests still rely on prompt rules + trope stripping
 * because operator-provided copy intentionally authorizes a wider vocabulary.
 */
export function stripKnownUnsupportedTropesFromDraft(
  draft: MarketingDraftFields,
  context: unknown,
): MarketingDraftFields {
  const corpus = buildClaimAuthorizationCorpus(context);
  const description = groundFreshDescriptionBody(draft.description, context);
  const short_description = stripLexicallyUngroundedFreshCopy(
    stripKnownUnsupportedTropeSentences(draft.short_description, corpus),
    context,
  );
  const seo_title = stripLexicallyUngroundedFreshCopy(
    stripKnownUnsupportedTropeSentences(draft.seo_title, corpus),
    context,
  );
  const meta_description = stripLexicallyUngroundedFreshCopy(
    stripKnownUnsupportedTropeSentences(draft.meta_description, corpus),
    context,
  );
  const tags = draft.tags.filter((tag) => {
    const hits = findKnownUnsupportedTropePhrases(tag);
    if (hits.length === 0) return true;
    return sentenceAuthorizedByCorpus(tag, corpus);
  });
  const seo_keywords = stripKnownUnsupportedTropeSentences(draft.seo_keywords, corpus);

  const factsRaw = (context && typeof context === "object"
    ? (context as { facts?: Record<string, unknown> }).facts
    : null) ?? {};
  const facts = {
    name: typeof factsRaw.name === "string" ? factsRaw.name : null,
    brand: typeof factsRaw.brand === "string" ? factsRaw.brand : null,
    category: typeof factsRaw.category === "string" ? factsRaw.category : null,
    options: Array.isArray(factsRaw.options) ? factsRaw.options.map(String) : [],
    values: Array.isArray(factsRaw.values) ? factsRaw.values.map(String) : [],
  };
  const extraAuthorized = extraAuthorizedTermsFromContext(context);

  const stripped: MarketingDraftFields = {
    description,
    short_description,
    seo_title,
    meta_description,
    tags,
    seo_keywords: seo_keywords || tags.join(", "),
  };

  if (!marketingDraftHasContent(stripped) || !stripped.description) {
    return buildNeutralProductCopy(facts);
  }

  // Preserve grounded body copy; polish SEO/keywords to customer-facing quality.
  return applyGroundedCopyQuality(stripped, facts, extraAuthorized);
}

/** @deprecated Use stripKnownUnsupportedTropesFromDraft — prior name overstated scope. */
export function groundMarketingDraft(
  draft: MarketingDraftFields,
  context: unknown,
): MarketingDraftFields {
  return stripKnownUnsupportedTropesFromDraft(draft, context);
}


function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const t = value.trim();
  return t.length ? t : null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
}

export function optionFactsFromVariants(
  variants: Array<{ option_values?: Record<string, string> | null } | null | undefined>,
): { optionNames: string[]; optionValues: string[] } {
  const names = new Set<string>();
  const values = new Set<string>();
  for (const variant of variants) {
    const ov = variant?.option_values;
    if (!ov || typeof ov !== "object") continue;
    for (const [k, v] of Object.entries(ov)) {
      if (k.trim()) names.add(k.trim());
      if (typeof v === "string" && v.trim()) values.add(v.trim());
    }
  }
  return { optionNames: Array.from(names), optionValues: Array.from(values) };
}

export interface ProductEnrichFacts {
  name: string;
  brand?: string | null;
  categoryName?: string | null;
  productType?: string | null;
  optionNames?: string[];
  optionValues?: string[];
}

export interface ExistingProductCopy {
  description?: string | null;
  shortDescription?: string | null;
  seoTitle?: string | null;
  metaDescription?: string | null;
  tags?: string[] | string | null;
  seoKeywords?: string | null;
}

export interface ProductEnrichRequest {
  facts: ProductEnrichFacts;
  rewrite?: boolean;
  existingCopy?: ExistingProductCopy;
}

/** Browser → orchestrator params. Extra keys are dropped. */
export function toProductEnrichParams(request: ProductEnrichRequest): Record<string, unknown> {
  const facts = request.facts;
  const params: Record<string, unknown> = {
    name: facts.name,
    brand: facts.brand ?? null,
    category_name: facts.categoryName ?? null,
    product_type: facts.productType ?? null,
    option_names: facts.optionNames ?? [],
    option_values: facts.optionValues ?? [],
    rewrite: request.rewrite === true,
  };
  if (request.rewrite === true && request.existingCopy) {
    const copy = request.existingCopy;
    const tags = Array.isArray(copy.tags)
      ? copy.tags.filter((t): t is string => typeof t === "string")
      : typeof copy.tags === "string"
        ? copy.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : [];
    params.existing_copy = {
      description: copy.description ?? null,
      short_description: copy.shortDescription ?? null,
      seo_title: copy.seoTitle ?? null,
      meta_description: copy.metaDescription ?? null,
      tags,
      seo_keywords: copy.seoKeywords ?? null,
    };
  }
  return params;
}

function pickExistingCopy(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const src = raw as Record<string, unknown>;
  const tags = asStringArray(src.tags);
  const out: Record<string, unknown> = {
    description: asTrimmedString(src.description),
    short_description: asTrimmedString(src.short_description),
    seo_title: asTrimmedString(src.seo_title),
    meta_description: asTrimmedString(src.meta_description),
    tags,
    seo_keywords: asTrimmedString(src.seo_keywords),
  };
  return out;
}

/**
 * Server-side context builder. Ignores SKU, prices, stock, tokens, and
 * existing copy unless rewrite is explicitly true.
 */
export function buildProductEnrichContext(params: unknown): {
  type: "product_facts";
  facts: Record<string, unknown>;
  rewrite: boolean;
  existing_copy?: Record<string, unknown>;
} {
  const p = params && typeof params === "object" ? (params as Record<string, unknown>) : {};
  const rewrite = p.rewrite === true;
  const facts = {
    name: asTrimmedString(p.name),
    brand: asTrimmedString(p.brand),
    category: asTrimmedString(p.category_name) ?? asTrimmedString(p.category),
    product_type: asTrimmedString(p.product_type),
    options: asStringArray(p.option_names),
    values: asStringArray(p.option_values),
  };
  const context: {
    type: "product_facts";
    facts: Record<string, unknown>;
    rewrite: boolean;
    existing_copy?: Record<string, unknown>;
  } = { type: "product_facts", facts, rewrite };
  if (rewrite) {
    const copy = pickExistingCopy(p.existing_copy);
    if (copy) context.existing_copy = copy;
  }
  return context;
}

export function contextContainsForbiddenProductFields(context: unknown): string[] {
  const found: string[] = [];
  const visit = (value: unknown, path: string) => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach((item, i) => visit(item, `${path}[${i}]`));
      return;
    }
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const key = k.toLowerCase();
      if ((FORBIDDEN_PRODUCT_CONTEXT_KEYS as readonly string[]).includes(key)) {
        found.push(path ? `${path}.${k}` : k);
      }
      visit(v, path ? `${path}.${k}` : k);
    }
  };
  visit(context, "");
  return found;
}

export interface MarketingDraftFields {
  description: string;
  short_description: string;
  seo_title: string;
  meta_description: string;
  tags: string[];
  seo_keywords: string;
}

type EmbeddedSeoKey = "seo_title" | "meta_description" | "seo_keywords";

function splitEmbeddedSeoSections(value: string): {
  description: string;
  seo_title: string;
  meta_description: string;
  seo_keywords: string;
} {
  const fields: Record<EmbeddedSeoKey, string> = {
    seo_title: "",
    meta_description: "",
    seo_keywords: "",
  };
  const kept: string[] = [];
  let pending: EmbeddedSeoKey | null = null;

  const labelToKey = (label: string): EmbeddedSeoKey => {
    const normalized = label.toLowerCase().replace(/\s+/g, " ");
    if (normalized === "seo title" || normalized === "meta title") return "seo_title";
    if (normalized === "meta description") return "meta_description";
    return "seo_keywords";
  };

  for (const line of value.replace(/\r\n?/g, "\n").split("\n")) {
    const match = line.match(/^\s*(?:[-*]\s*)?(?:\*\*|__)?(seo title|meta title|meta description|seo keywords|search keywords|meta keywords|keywords)\s*:?\s*(?:\*\*|__)?\s*(.*?)\s*$/i);
    if (match) {
      const key = labelToKey(match[1]);
      const inlineValue = match[2].trim();
      if (inlineValue) {
        if (!fields[key]) fields[key] = inlineValue;
        pending = null;
      } else {
        pending = key;
      }
      continue;
    }
    if (pending && line.trim()) {
      if (!fields[pending]) fields[pending] = line.trim();
      pending = null;
      continue;
    }
    if (!pending) kept.push(line);
  }

  return {
    description: kept.join("\n").replace(/\n{3,}/g, "\n\n").trim(),
    ...fields,
  };
}

/** Drop any non-marketing keys and recover SEO fields accidentally embedded in description. */
export function pickMarketingDraft(result: unknown): MarketingDraftFields {
  const r = result && typeof result === "object" ? (result as Record<string, unknown>) : {};
  const embedded = splitEmbeddedSeoSections(asTrimmedString(r.description) ?? "");
  const description = embedded.description;
  const short_description = asTrimmedString(r.short_description) ?? asTrimmedString(r.shortDescription) ?? "";
  const seo_title = asTrimmedString(r.seo_title) ?? asTrimmedString(r.seoTitle) ?? embedded.seo_title;
  const meta_description = asTrimmedString(r.meta_description) ?? asTrimmedString(r.metaDescription) ?? embedded.meta_description;
  const tags = asStringArray(r.tags).slice(0, 12);
  const seo_keywords = asTrimmedString(r.seo_keywords) ?? asTrimmedString(r.seoKeywords) ?? (embedded.seo_keywords || tags.join(", "));
  return { description, short_description, seo_title, meta_description, tags, seo_keywords };
}

export function marketingDraftHasContent(draft: MarketingDraftFields): boolean {
  return Boolean(draft.description || draft.short_description || draft.seo_title || draft.meta_description);
}

export function frontendMustNotCarryOpenAiSecret(source: string): boolean {
  if (/\bVITE_[A-Z0-9_]*OPENAI[A-Z0-9_]*\b/.test(source)) return false;
  if (/\bOPENAI_API_KEY\s*=/.test(source)) return false;
  if (/import\.meta\.env\.[A-Z0-9_]*OPENAI/.test(source)) return false;
  return true;
}
